import { useEffect, useState } from 'react';
import { useAppSelector } from './redux';
import { locationAPI } from '../services/api';

const BANGALORE_LAT = 12.971598;
const BANGALORE_LNG = 77.594563;
const MAX_RADIUS_KM = 100.0;

function calculateHaversineDistance(lat1: number, lng1: number, lat2: number, lng2: number): number {
  const R = 6371; // Earth radius in km
  const dLat = (lat2 - lat1) * (Math.PI / 180);
  const dLng = (lng2 - lng1) * (Math.PI / 180);
  const a =
    Math.sin(dLat / 2) * Math.sin(dLat / 2) +
    Math.cos(lat1 * (Math.PI / 180)) *
      Math.cos(lat2 * (Math.PI / 180)) *
      Math.sin(dLng / 2) *
      Math.sin(dLng / 2);
  const c = 2 * Math.atan2(Math.sqrt(a), Math.sqrt(1 - a));
  return R * c;
}

export const BANGALORE_KEYWORDS = [
  'bangalore', 'bengaluru', 'blr', 'indiranagar', 'koramangala',
  'whitefield', 'hsr', 'jp nagar', 'jayanagar', 'electronic city',
  'yelahanka', 'marathahalli', 'tumkur', 'tumakuru', 'hosur', 'kolar',
  'ramanagara', 'chikkaballapur', 'chikkaballapura', 'karnataka',
];

export function isUserAccountBangalore(user?: any): boolean {
  if (!user) return false;
  const loc = (
    (user.location || '') + ' ' +
    (user.address || '') + ' ' +
    (user.city || '')
  ).toLowerCase();
  return BANGALORE_KEYWORDS.some((kw) => loc.includes(kw));
}

export function useAutoGeolocation() {
  const { user, isAuthenticated } = useAppSelector((state) => state.auth);
  const [coords, setCoords] = useState<{ latitude: number; longitude: number } | null>(null);
  const [distanceKm, setDistanceKm] = useState<number | null>(null);
  const [isOutsideRadius, setIsOutsideRadius] = useState<boolean>(false);
  const [permissionState, setPermissionState] = useState<'prompt' | 'granted' | 'denied'>('prompt');
  const [loading, setLoading] = useState<boolean>(true);

  const isBangaloreAccount = isUserAccountBangalore(user);

  const requestLocation = () => {
    // If user account is already Bangalore, NEVER show radius interest modal
    if (isBangaloreAccount) {
      setIsOutsideRadius(false);
      setLoading(false);
      if (navigator.geolocation) {
        navigator.geolocation.getCurrentPosition(
          (position) => {
            const lat = position.coords.latitude;
            const lng = position.coords.longitude;
            setCoords({ latitude: lat, longitude: lng });
            if (isAuthenticated && user?.id) {
              locationAPI.updateUserLocation({
                latitude: lat,
                longitude: lng,
                accuracy: Math.round(position.coords.accuracy || 0),
              }).catch(() => {});
            }
          },
          () => {},
          { timeout: 8000 }
        );
      }
      return;
    }

    if (!navigator.geolocation) {
      setLoading(false);
      return;
    }

    setLoading(true);
    navigator.geolocation.getCurrentPosition(
      async (position) => {
        const lat = position.coords.latitude;
        const lng = position.coords.longitude;
        const dist = calculateHaversineDistance(lat, lng, BANGALORE_LAT, BANGALORE_LNG);

        setCoords({ latitude: lat, longitude: lng });
        setDistanceKm(dist);
        setPermissionState('granted');
        setLoading(false);

        // Account is only outside radius if distance > 100km AND not already Bangalore account
        const outside = dist > MAX_RADIUS_KM && !isUserAccountBangalore(user);
        setIsOutsideRadius(outside);

        // Sync live GPS coordinates to backend if user is authenticated
        if (isAuthenticated && user?.id) {
          try {
            await locationAPI.updateUserLocation({
              latitude: lat,
              longitude: lng,
              accuracy: Math.round(position.coords.accuracy || 0),
            });
          } catch (err) {
            console.error('Failed to sync live GPS location:', err);
          }
        }
      },
      (error) => {
        console.warn('Browser geolocation prompt error or permission denied:', error.message);
        setPermissionState('denied');
        setLoading(false);

        // Fallback: If user's profile location is Bangalore, never mark outside
        if (isUserAccountBangalore(user)) {
          setIsOutsideRadius(false);
        } else if (user?.location || user?.address) {
          const loc = (user.location || user.address || '').toLowerCase();
          const isLocal = BANGALORE_KEYWORDS.some((kw) => loc.includes(kw));
          setIsOutsideRadius(!isLocal);
        }
      },
      { enableHighAccuracy: true, timeout: 12000, maximumAge: 60000 }
    );
  };

  useEffect(() => {
    // Listen for permission state changes (e.g. when user enables location in Chrome settings lock icon)
    if (navigator.permissions && navigator.permissions.query) {
      navigator.permissions
        .query({ name: 'geolocation' })
        .then((result) => {
          setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
          result.onchange = () => {
            setPermissionState(result.state as 'prompt' | 'granted' | 'denied');
            if (result.state === 'granted') {
              requestLocation();
            }
          };
        })
        .catch(() => {});
    }

    requestLocation();
  }, [isAuthenticated, user?.id]);

  return {
    coords,
    distanceKm,
    isOutsideRadius,
    permissionState,
    loading,
    setIsOutsideRadius,
    requestLocation,
  };
}
